import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { orderAPI } from "../services/api";
import toast from "react-hot-toast";

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [orderSaved, setOrderSaved] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Save the pending order to database when confirmation page loads
    // This ensures orders are only saved after user reaches confirmation
    const savePendingOrder = async () => {
      if (isProcessing) return; // Prevent duplicate processing

      setIsProcessing(true);

      try {
        const pendingOrderData = localStorage.getItem("pendingOrder");

        if (pendingOrderData) {
          const orderData = JSON.parse(pendingOrderData);

          // Save order to database
          const savedOrder = await orderAPI.create(orderData);
          console.log("Order saved to database:", savedOrder.data);

          // Clear the pending order from localStorage
          localStorage.removeItem("pendingOrder");

          setOrderSaved(true);
          setIsProcessing(false);
        } else {
          // No pending order found, redirect to home
          console.warn("No pending order found, redirecting to home");
          toast.error("No order found. Please place an order first.");
          setIsProcessing(false);
          navigate("/");
          return;
        }
      } catch (error) {
        console.error("Error saving order to database:", error);

        if (retryCount < 3) {
          setRetryCount((prev) => prev + 1);
          toast.error(
            `Failed to save order. Retrying... (${retryCount + 1}/3)`
          );

          // Retry after 2 seconds
          setTimeout(() => {
            savePendingOrder();
          }, 2000);
        } else {
          toast.error(
            "Failed to save order after multiple attempts. Please contact support."
          );
          // Don't clear localStorage so user can try again later
          setIsProcessing(false);
          navigate("/");
        }
      }
    };

    savePendingOrder();

    const timer = setTimeout(() => {
      navigate("/");
    }, 6000);

    // Warn user if they try to leave before order is saved
    const handleBeforeUnload = (e) => {
      if (!orderSaved && localStorage.getItem("pendingOrder")) {
        e.preventDefault();
        e.returnValue =
          "Your order is still being processed. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigate, retryCount, orderSaved]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 relative overflow-hidden">
      {/* Sparkles and Glitter Animation */}
      <div className="sparkles-container">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="sparkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Confetti */}
      <div className="confetti-container">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="confetti"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              backgroundColor: [
                "#305669",
                "#8ABEB9",
                "#B7E5CD",
                "#10B981",
                "#34D399",
              ][Math.floor(Math.random() * 5)],
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center animate-scale-in">
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md mx-4">
          {/* Success Icon with Pulse */}
          <div className="mb-6 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-green-100 rounded-full animate-ping-slow opacity-75"></div>
            </div>
            <div className="relative flex items-center justify-center">
              <CheckCircle className="w-32 h-32 text-green-500 animate-bounce-slow" />
            </div>
          </div>

          {/* Success Message */}
          {orderSaved ? (
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-4 animate-fade-in">
                Order Confirmed! 🎉
              </h1>
              <p className="text-lg text-gray-600 mb-4 animate-fade-in-delay">
                Thank you for your purchase!
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 animate-fade-in-delay">
                <p className="text-sm text-green-800 flex items-center justify-center gap-2">
                  <span>You will receive order confirmation on WhatsApp</span>
                </p>
              </div>
              <p className="text-sm text-gray-500 animate-fade-in-delay-2">
                Redirecting to homepage in a moment...
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-gray-900 mb-4 animate-fade-in">
                Processing Order...
              </h1>
              <p className="text-lg text-gray-600 mb-4 animate-fade-in-delay">
                Please wait while we confirm your order
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 animate-fade-in-delay">
                <p className="text-sm text-blue-800 flex items-center justify-center gap-2">
                  <span>Saving your order details...</span>
                </p>
                {retryCount > 0 && (
                  <p className="text-xs text-blue-600 mt-2">
                    Retry attempt: {retryCount}/3
                  </p>
                )}
              </div>
            </>
          )}

          {/* Loading Bar */}
          {orderSaved && (
            <div className="mt-8 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full animate-progress"></div>
            </div>
          )}

          {!orderSaved && (
            <div className="mt-8 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-400 to-blue-500 h-full rounded-full animate-pulse"></div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1) rotate(180deg);
          }
        }

        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes scale-in {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes progress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.75;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.5;
          }
          100% {
            transform: scale(1);
            opacity: 0.75;
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .sparkle {
          position: absolute;
          width: 8px;
          height: 8px;
          background: linear-gradient(45deg, #ffd700, #ffed4e, #fff);
          border-radius: 50%;
          animation: sparkle 3s infinite;
          box-shadow: 0 0 10px #ffd700;
        }

        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 4s linear infinite;
        }

        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out 0.3s both;
        }

        .animate-fade-in-delay {
          animation: fade-in 0.8s ease-out 0.6s both;
        }

        .animate-fade-in-delay-2 {
          animation: fade-in 0.8s ease-out 0.9s both;
        }

        .animate-progress {
          animation: progress 6s linear;
        }

        .animate-ping-slow {
          animation: ping-slow 2s ease-in-out infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default OrderConfirmation;
